<template>
  <div class="guide-video-banner">
    <div class="guide-video-banner-video" @click="openOverlay">
      <img 
        :src="videoThumbnail" 
        alt="Video thumbnail" 
        @error="handleImageError"
        :style="{ display: imageError ? 'none' : 'block' }"
      />
      <div v-if="imageError" class="video-thumbnail-placeholder">
        <svg xmlns="http://www.w3.org/2000/svg" width="159" height="89" viewBox="0 0 159 89">
          <rect width="159" height="89" fill="#E5E7EB"/>
          <path d="M79.5 44.5L65 35v19l14.5-9.5z" fill="#9CA3AF"/>
        </svg>
      </div>
      <div class="play-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
          <foreignObject x="-11.2" y="-11.2" width="78.4" height="78.4">
            <div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(5.6px);clip-path:url(#bgblur_0_21517_383981_clip_path);height:100%;width:100%"></div>
          </foreignObject>
          <g data-figma-bg-blur-radius="11.2">
            <path d="M28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28C0 12.536 12.536 0 28 0ZM23.625 17.7295C22.4585 17.0775 21.0003 17.8924 21 19.1963V36.8037C21.0003 38.1076 22.4585 38.9225 23.625 38.2705L39.375 29.4668C40.5414 28.8147 40.5414 27.1853 39.375 26.5332L23.625 17.7295Z" fill="white" fill-opacity="0.3"/>
          </g>
          <defs>
            <clipPath id="bgblur_0_21517_383981_clip_path" transform="translate(11.2 11.2)">
              <path d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z"/>
            </clipPath>
          </defs>
        </svg>
      </div>
    </div>
    <div class="guide-video-banner-content">
      <div class="guide-video-banner-content-title">
        <span>{{ text }}</span>
      </div>
      <div class="guide-video-banner-content-description">
        <p class="text-bold">
          {{ subtext }}
        </p>
      </div>
      <div @click.stop="openOverlay">
        <span class="watch-button">Watch Video</span>
      </div>
    </div>
    <!-- Video Overlay -->
    <div v-if="isOverlayOpen" class="video-overlay" @click="closeOverlay">
      <div class="video-overlay-content" @click.stop>
        <button class="video-overlay-close" @click="closeOverlay">
          <CloseIcon color="light" size="normal" />
        </button>
        <iframe
          v-if="isEmbedUrl"
          ref="videoRef"
          :src="videoUrl"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowfullscreen
        ></iframe>
        <video
          v-else
          ref="videoRef"
          :src="videoUrl"
          controls
          autoplay
          @ended="closeOverlay"
        ></video>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, computed } from 'vue';
import CloseIcon from '@components/icons/items/Interface/CloseIcon.vue';

const props = defineProps({
    text: {
        type: String,
        required: true,
    },
    subtext: {
        type: String,
        required: true,
    },
    videoUrl: {
        type: String,
        required: true,
    },
    videoThumbnail: {
        type: String,
        required: true,
    },
});

const videoRef = ref(null);
const isOverlayOpen = ref(false);
const imageError = ref(false);

// Check if the URL is an embed URL (iframe-based)
const isEmbedUrl = computed(() => {
    return props.videoUrl.includes('embed.api.video') ||
           props.videoUrl.includes('youtube.com/embed') ||
           props.videoUrl.includes('vimeo.com/video');
});

const openOverlay = async () => {
    isOverlayOpen.value = true;
    await nextTick();
    if (videoRef.value && !isEmbedUrl.value) {
        videoRef.value.play().catch(() => {});
    }
};

const closeOverlay = () => {
    // Only try to pause if it's a regular video element, not an iframe
    if (videoRef.value && !isEmbedUrl.value) {
        videoRef.value.pause();
    }
    isOverlayOpen.value = false;
};

const handleImageError = () => {
    imageError.value = true;
};
</script>

<style scoped>
.guide-video-banner {
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 16px;
  border-radius: 12px;
  border: 1px solid #d0d5dd;
  background: linear-gradient(90deg, #2e5bf2 0%, #8b22f1 100%);
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  position: relative;
  margin-top: 16px;
}

.guide-video-banner-content {
    color: #FFF;
    font-family: Inter;
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1;
    min-width: 0;
}

.guide-video-banner-content-title {
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
}

.guide-video-banner-content-description {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
}

.guide-video-banner-content-description > p {
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.guide-video-banner-video {
    width: 159px;
    height: 89px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;
    cursor: pointer;
}

.guide-video-banner-video img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.video-thumbnail-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #E5E7EB;
}

.video-thumbnail-placeholder svg {
    width: 100%;
    height: 100%;
}

.play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.watch-button {
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.video-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.video-overlay-content {
    position: relative;
    width: 90%;
    max-width: 900px;
}

.video-overlay-content video,
.video-overlay-content iframe {
    width: 100%;
    height: 506px;
    border-radius: 12px;
    display: block;
}

.video-overlay-close {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
    transition: opacity 0.2s, background 0.2s;
    z-index: 1;
}

.video-overlay-close:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.7);
}
</style>
